import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useConfirm, useToast } from '@/ui';
import { useAuth } from '@/app/AuthContext.jsx';
import {
  createDiligenciaByCorte,
  errorMessage,
  getCase,
  getTask,
} from '@/lib/recepthor-api';
import { qk } from '@/lib/queryKeys';
import SolicitudCotizacionesResumen from './SolicitudCotizacionesResumen';

// Shared create-diligencia flow behind the NuevaDiligenciaModal (list + causa).
// `create(payload, send)`:
//   send=false → persist a DRAFT (no emails).
//   send=true  → confirm (irreversible), then create + email the corte → QUOTING.
// On success invalidates the active set, reads the new detail and navigates
// to it.
//
// Invalidating only qk.activeTask.all() left the /diligencias board
// (useActiveLaneTasks, useLaneCounts, useActiveDiligencias) on the stale set
// until a reload. Their key prefixes are the literal strings the hooks pass
// ('tasks-active', 'lane-counts', 'active-diligencias'), not a shared 'tasks'
// root, so each one is invalidated by name. The promise settles once the
// active queries refetched, so the caller navigates to fresh data.
export function invalidateAfterCreateDiligencia(queryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: qk.activeTask.all() }),
    queryClient.invalidateQueries({ queryKey: ['tasks-active'] }),
    queryClient.invalidateQueries({ queryKey: ['lane-counts'] }),
    queryClient.invalidateQueries({ queryKey: ['active-diligencias'] }),
  ]);
}

// Reads the new diligencia and its causa into the cache before navigating to
// it, under the keys VerDiligencia reads (useTaskDetail, useCaseDetail).
// Without it the detail drew every field as "—" until both reads came back,
// some 4 seconds in Beta. A failed read is not thrown and not retried, so the
// navigation never waits on backoff: the detail asks again and shows its own
// error. A read still pending at the deadline is not waited on either: the
// navigation goes ahead, and the detail's own query joins the read in flight.
export const PREFETCH_DEADLINE_MS = 3000;

// Settles with `promise` or after `ms`, whichever comes first. What is still
// pending keeps running; only the wait on it ends.
export function withDeadline(promise, ms) {
  let timer;
  const deadline = new Promise((resolve) => {
    timer = setTimeout(resolve, ms);
  });
  return Promise.race([promise, deadline]).finally(() => clearTimeout(timer));
}

export function prefetchCreatedDiligencia(
  queryClient,
  { taskId, caseId, organizationId },
  { deadlineMs = PREFETCH_DEADLINE_MS } = {},
) {
  const reads = Promise.all([
    queryClient.prefetchQuery({
      queryKey: qk.taskDetail(taskId),
      queryFn: () => getTask(taskId, { organizationId }),
      retry: false,
    }),
    caseId
      ? queryClient.prefetchQuery({
          queryKey: qk.caseDetail(caseId),
          queryFn: () => getCase(caseId, { organizationId }),
          retry: false,
        })
      : null,
  ]);
  return withDeadline(reads, deadlineMs);
}

export function useCreateDiligencia() {
  const organizationId = useAuth()?.organizationId ?? null;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);

  const create = useCallback(
    async (payload, send, caseContext = {}) => {
      if (busy) return;
      if (send) {
        const ok = await confirm({
          title: 'Solicitar cotizaciones',
          body: (
            <>
              <p className="confirm-lead">
                Se envía la solicitud a los receptores de la corte. Es
                irreversible: tras enviar, los datos de la diligencia quedan
                fijos.
              </p>
              <SolicitudCotizacionesResumen
                data={{ ...caseContext, ...payload }}
              />
            </>
          ),
          confirmLabel: 'Solicitar cotizaciones',
          confirmVariant: 'seal',
        });
        if (!ok) return;
      }
      setBusy(true);
      const toastId = toast({
        kind: 'loading',
        message: send ? 'Enviando solicitudes…' : 'Guardando borrador…',
        dismissLabel: 'en curso',
      });
      try {
        const created = await createDiligenciaByCorte({
          ...payload,
          send: Boolean(send),
        });
        // The board refetch gets the same deadline as the detail reads: a
        // request that hangs must not hold the creation screen.
        await Promise.all([
          withDeadline(
            invalidateAfterCreateDiligencia(queryClient),
            PREFETCH_DEADLINE_MS,
          ),
          created?.id
            ? prefetchCreatedDiligencia(queryClient, {
                taskId: created.id,
                caseId: created.caseId ?? payload.caseId ?? null,
                organizationId,
              })
            : null,
        ]);
        toast.update(toastId, {
          kind: 'success',
          message: send ? 'Solicitudes enviadas.' : 'Borrador guardado.',
          duration: 4000,
        });
        // `replace`: the modal route must not survive in history. Pushing would
        // leave /diligencias/nueva behind the new detail, so Back would reopen
        // the create modal (prefilled) instead of the view it opened over.
        if (created?.id)
          navigate(`/diligencias/${created.id}`, { replace: true });
      } catch (err) {
        console.error('createDiligenciaByCorte failed', err);
        toast.update(toastId, {
          kind: 'err',
          // El 409 del gate llega con el mensaje del catálogo, en inglés. No se
          // traduce acá: ese módulo declara seis conflictos distintos y los seis
          // serializan con el mismo `name`, `ConflictError`, así que ni el estado
          // ni el nombre dicen cuál ocurrió. Una frase por estado diría «la causa
          // no está verificada» sobre una diligencia ya en curso y sobre cuatro
          // casos más. Distinguirlos necesita que el API exponga un
          // discriminador, y eso no es de este lado.
          message: errorMessage(
            err,
            send
              ? 'No se pudieron enviar las solicitudes.'
              : 'No se pudo guardar el borrador.',
          ),
          duration: 0,
        });
        setBusy(false);
      }
    },
    [busy, confirm, toast, queryClient, navigate, organizationId],
  );

  return { create, busy };
}

export default useCreateDiligencia;
